import { inject, Injectable, NgZone } from '@angular/core';
import {
  bufferTime,
  distinctUntilChanged,
  filter,
  finalize,
  from,
  fromEvent,
  interval,
  map,
  merge,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
  tap,
  timer,
} from 'rxjs';
import { USER_IDLE_CONFIG } from './user-idle.token';
import { UserIdleConfig } from './user-idle.type';

/**
 * User's idle service.
 * The main schema will be as follow:
 * | ––2m (ping) ––4m (ping) ––6m (ping) ... -–10m (user idle detected, start timer for 5 min) ––12m (ping) ––14m (ping) ––15m (time is out) |
 * If you don't use a ping just set ping to any value (not null) and just ignore it.
 */
@Injectable()
export class UserIdleService {
  ping$!: Observable<any>;

  /**
   * Events that can interrupts user's inactivity timer.
   */
  protected activityEvents$!: Observable<any>;

  protected timerStart$ = new Subject<boolean>();
  protected idleDetected$ = new Subject<boolean>();
  protected timeout$ = new Subject<boolean>();
  protected idle$!: Observable<any>;
  protected timer$!: Observable<any>;

  /**
   * Idle value in milliseconds.
   * Default equals to 10 minutes.
   */
  protected idleMillisec = 600 * 1000;

  /**
   * Idle buffer wait time milliseconds to collect user action
   * Default equals to 1 Sec.
   */
  protected idleSensitivityMillisec = 1000;

  /**
   * Timeout value in seconds.
   * Default equals to 5 minutes.
   */
  protected timeout = 300;

  /**
   * Ping value in milliseconds.
   * Default equals to 2 minutes.
   */
  protected pingMillisec = 120 * 1000;

  /**
   * Timeout status.
   */
  protected isTimeout = false;

  /**
   * Timer of user's inactivity is in progress.
   */
  protected isInactivityTimer = false;
  protected isIdleDetected = false;

  protected idleSubscription!: Subscription;

  private _ngZone = inject(NgZone);
  private _config = inject(USER_IDLE_CONFIG, { optional: true });

  constructor() {
    if (this._config) {
      this.setConfig(this._config);
    }
  }

  /**
   * Start watching for user idle and setup timer and ping.
   */
  startWatching() {
    if (!this.activityEvents$) {
      this.activityEvents$ = merge(fromEvent(window, 'mousemove'), fromEvent(document, 'keydown'));
    }

    this.idle$ = from(this.activityEvents$);

    if (this.idleSubscription) {
      this.idleSubscription.unsubscribe();
    }

    console.log('Idle timer started');

    // Run outside angular must be wrap the subscribe (https://github.com/angular/angular/issues/26187#issuecomment-425894624)
    this._ngZone.runOutsideAngular(
      () =>
        // If any of user events is not active for idle-seconds when start timer.
        (this.idleSubscription = this.idle$
          .pipe(
            // Starting point of detecting of user's inactivity, equivalent to debounceTime, but store all the events
            bufferTime(this.idleSensitivityMillisec),

            // Filter events that are not user's inactivity: no event(!arr.length), last idleSensitivityMillisec is not idle (the rest of the operations)
            filter(arr => !arr.length && !this.isIdleDetected && !this.isInactivityTimer),

            // Indicate that user's inactivity is detected (within idle-sensitivity-millisec)
            tap(() => {
              this.isIdleDetected = true;
              this.idleDetected$.next(true);
            }),

            switchMap(() =>
              interval(1000).pipe(
                takeUntil(
                  merge(
                    // 1. user's activity is detected
                    this.activityEvents$,

                    // 2. timer is out
                    timer(this.idleMillisec).pipe(
                      tap(() => {
                        this._ngZone.run(() => {
                          this.isInactivityTimer = true;
                          this.timerStart$.next(true);
                        });
                      })
                    )
                  )
                ),
                // Reset the idle flag
                finalize(() => {
                  this.isIdleDetected = false;
                  this.idleDetected$.next(false);
                })
              )
            )
          )
          .subscribe())
    );

    this.setupTimer(this.timeout);
    this.setupPing(this.pingMillisec);
  }

  stopWatching() {
    this.stopTimer();
    if (this.idleSubscription) {
      this.idleSubscription.unsubscribe();
    }
  }

  stopTimer() {
    this.isInactivityTimer = false;
    this.timerStart$.next(false);
  }

  resetTimer() {
    this.stopTimer();
    this.isTimeout = false;
  }

  /**
   * Return observable for timer's countdown number that emits after idle.
   */
  onTimerStart(): Observable<number> {
    return this.timerStart$.pipe(
      distinctUntilChanged(),
      switchMap(start => (start ? this.timer$ : of(null)))
    );
  }

  /**
   * Return observable for idle status changed
   */
  onIdleStatusChanged(): Observable<boolean> {
    return this.idleDetected$.asObservable();
  }

  /**
   * Return observable for timeout is fired.
   */
  onTimeout(): Observable<boolean> {
    return this.timeout$.pipe(
      filter(timeout => !!timeout),
      tap(() => (this.isTimeout = true)),
      map(() => true)
    );
  }

  getConfigValue(): UserIdleConfig {
    return {
      idle: this.idleMillisec / 1000,
      timeout: this.timeout,
      ping: this.pingMillisec / 1000,
    };
  }

  /**
   * Set config values.
   * @param config
   */
  setConfigValues(config: UserIdleConfig) {
    if (this.idleSubscription && !this.idleSubscription.closed) {
      console.error('Call stopWatching() before set config values');
      return;
    }

    this.setConfig(config);
  }

  private setConfig(config: UserIdleConfig) {
    if (config.idle) {
      this.idleMillisec = config.idle * 1000;
    }
    if (config.ping) {
      this.pingMillisec = config.ping * 1000;
    }
    if (config.timeout) {
      this.timeout = config.timeout;
    }
  }

  /**
   * Set custom activity events
   *
   * @param customEvents Example: merge(
   *   fromEvent(window, 'mousemove'),
   *   fromEvent(window, 'resize'),
   *   fromEvent(document, 'keydown'),
   *   fromEvent(document, 'touchstart'),
   *   fromEvent(document, 'touchend')
   * )
   */
  setCustomActivityEvents(customEvents: Observable<any>) {
    if (this.idleSubscription && !this.idleSubscription.closed) {
      console.error('Call stopWatching() before set custom activity events');
      return;
    }

    this.activityEvents$ = customEvents;
  }

  /**
   * Setup timer.
   *
   * Counts every seconds and return n+1 and fire timeout for last count.
   * @param timeout Timeout in seconds.
   */
  protected setupTimer(timeout: number) {
    this._ngZone.runOutsideAngular(() => {
      this.timer$ = of(() => new Date()).pipe(
        map(fn => fn()),
        switchMap(startDate =>
          interval(1000).pipe(
            map(() => Math.round((new Date().valueOf() - startDate.valueOf()) / 1000)), //   convert elapsed count to seconds
            tap(elapsed => {
              if (elapsed >= timeout) {
                this.timeout$.next(true);
              }
            })
          )
        )
      );
    });
  }

  /**
   * Setup ping.
   *
   * Pings every ping-seconds only if is not timeout.
   * @param pingMillisec
   */
  protected setupPing(pingMillisec: number) {
    this.ping$ = interval(pingMillisec).pipe(filter(() => !this.isTimeout));
  }
}
